import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  useAdForgeGetDefaultsQuery,
  useAdForgeUpdateDefaultsMutation,
} from "~/ui-rtk/api/adForgeApi"
import { Chip } from "~/ui-rtk/components/ui/common"
import { Button } from "~/ui-rtk/components/ui/controls"
import Input from "~/ui-rtk/components/ui/controls/Input"

/**
 * Company-level UTM/URL-parameter pattern, edited as the query string
 * marketers already copy around (`utm_source=facebook&utm_medium=cpc`).
 * Stored structured (Record<string, string>) on AdForge defaults; applied
 * per creative at launch via Meta's url_tags, and read by agents through
 * the MCP `adforge_setup` tool so they never have to guess the pattern.
 */

/** `utm_source=facebook&utm_medium=cpc` → { utm_source: 'facebook', ... } */
export function parseUrlParameters(text: string): Record<string, string> {
  const params: Record<string, string> = {}
  for (const [key, value] of new URLSearchParams(text.trim())) {
    if (key.trim()) params[key.trim()] = value
  }
  return params
}

/** { utm_source: 'facebook' } → `utm_source=facebook` (decoded for display) */
export function formatUrlParameters(
  params: Record<string, string> | undefined,
): string {
  if (!params) return ""
  return Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join("&")
}

export default function UtmSettings() {
  const { data: defaults, isLoading } = useAdForgeGetDefaultsQuery()
  const [updateDefaults, { isLoading: isSaving }] =
    useAdForgeUpdateDefaultsMutation()

  const [text, setText] = useState("")

  useEffect(() => {
    setText(formatUrlParameters(defaults?.urlParameters))
  }, [defaults?.urlParameters])

  const parsed = useMemo(() => parseUrlParameters(text), [text])
  const pairs = Object.entries(parsed)

  const handleSave = async () => {
    try {
      // {} is an explicit clear server-side; undefined would preserve.
      await updateDefaults({ urlParameters: parsed }).unwrap()
      toast.success(
        pairs.length === 0
          ? "UTM pattern cleared"
          : `UTM pattern saved — ${formatUrlParameters(parsed)}`,
      )
    } catch {
      toast.error("Couldn't save the UTM pattern")
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <h3>UTM parameters</h3>
        <p className="text-sm text-system-gray-400">Loading…</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3>UTM parameters</h3>
        <p className="max-w-3xl text-sm text-system-gray-400">
          The company&apos;s UTM pattern for paid ads. Agents and AdForge
          tooling read this and attach it to creatives they build (Meta applies
          it at click-time via url_tags) instead of guessing a pattern — which
          keeps attribution consistent.
        </p>
      </div>

      <div className="max-w-3xl space-y-2">
        <label
          htmlFor="utm-parameters"
          className="text-xs font-medium text-system-gray-400"
        >
          Pattern (query-string form)
        </label>
        <Input
          id="utm-parameters"
          className="w-full font-mono"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="utm_source=facebook&utm_medium=cpc&utm_campaign={{campaign.name}}"
        />
        {pairs.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {pairs.map(([key, value]) => (
              <Chip key={key}>
                <span className="font-mono text-xs">
                  {key}={value}
                </span>
              </Chip>
            ))}
          </div>
        )}
        {text.trim() !== "" && pairs.length === 0 && (
          <p className="text-xs text-system-red-400">
            Nothing parseable — use key=value pairs joined by &amp;.
          </p>
        )}
      </div>

      <Button
        variant={{ variant: "solid", color: "pink" }}
        size="sm"
        type="button"
        isLoading={isSaving}
        disabled={text.trim() !== "" && pairs.length === 0}
        onClick={() => void handleSave()}
      >
        Save UTM pattern
      </Button>
    </div>
  )
}
